import paymentMethodTypes from 'common/constants/paymentMethodTypes'
import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import useManagement, { selectors } from 'publisher/hooks/useManagement'
import usePage, { selectors as pageSelectors } from 'publisher/hooks/usePage'
import usePayment, {
  selectors as paymentSelectors,
} from 'publisher/hooks/usePayment'
import React, { useEffect, useState } from 'react'
import { useCalculateButtonStyles } from 'src/publisher/shared/hooks/use-calculate-button-styles'
import { useSelectedButtonColor } from 'src/publisher/shared/hooks/use-theme-color/use-selected-button-color'
import { Button as CommonButton } from 'src/shared/components/button/button'
import { UpsellButtonEntityInterface } from 'src/shared/entities/upsell-button/upsell-button.schema'
import CashOnDeliveryUpsellButton from './CashOnDeliveryUpsellButton'
import DLocalCreditCardUpsellButton from './DLocalCreditCardUpsellButton'
import FlutterwaveUpsellButton from './FlutterwaveUpsellButton'
import GocardlessUpsellButton from './GocardlessUpsellButton'
import MercadoPagoUpsellButton from './MercadoPagoUpsellButton'
import MollieUpsellButton from './MollieUpsellButton'
import PaypalUpsellButton from './PaypalUpsellButton'
import PaystackUpsellButton from './PaystackUpsellButton'
import PaypalUpsellRestButton from './paypal-upsell-rest-button'
import RazorpayUpsellButton from './RazorpayUpsellButton'
import SecureAndPayUpsellButton from './SecureAndPayUpsellButton'
import StripeCreditCardUpsellButton from './StripeCreditCardUpsellButton'
import StripeSepaDebitUpsellButton from './StripeSepaDebitUpsellButton'
import XenditUpsellButton from './XenditUpsellButton'

export type UpsellPaymentButtonPropsType = {
  entity: UpsellButtonEntityInterface
}

function UpsellPaymentButton({ entity }: UpsellPaymentButtonPropsType) {
  const isScreenshotMode = useManagement(selectors.isScreenshotMode)
  const paymentMethod = usePayment(paymentSelectors.getActivePaymentMethod)
  const isPreview = useManagement(selectors.isPreviewMode)
  const isTemplate = usePage(pageSelectors.isTemplate)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    // calculatedTextAlign,
    // calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useCalculateButtonStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    buttonType: entity.buttonType,
    fontSize: entity.textFontSize,
    mobileFontSize: entity.mobileTextFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    fontFamily: entity.textFontFamily,
    mobileFontFamily: entity.mobileTextFontFamily,
    fontWeight: entity.textFontWeight,
    mobileFontWeight: entity.mobileTextFontWeight,
    fontStyle: entity.textFontStyle,
    mobileFontStyle: entity.mobileTextFontStyle,
  })

  const {
    buttonTextColor,
    buttonBackgroundColor,
    hoveredButtonBackgroundColor,
  } = useSelectedButtonColor(entity.buttonType)

  if (isScreenshotMode || isPreview || isTemplate) {
    return (
      <StyleWrapper
        flex
        flexColumn
        margin={entity.margin}
        mobileMargin={entity.mobileMargin}
        appearance={entity.appearance}
      >
        <CommonButton
          text={entity.text}
          border={calculatedBorder.desktop}
          mobileBorder={calculatedBorder.mobile}
          subText={entity.subText}
          padding={calculatedPadding.desktop}
          mobilePadding={calculatedPadding.mobile}
          alignSelf={entity.alignSelf}
          background={
            (entity.background ?? entity.backgroundColor) ||
            buttonBackgroundColor
          }
          mobileBackground={
            (entity.mobileBackground ?? entity.mobileBackgroundColor) ||
            buttonBackgroundColor
          }
          attrId={entity.htmlAttrId}
          width={entity.width}
          mobileWidth={entity.mobileWidth}
          textColor={entity.textColor || buttonTextColor}
          subTextColor={entity.subTextColor}
          mobileTextColor={entity.mobileTextColor || buttonTextColor}
          mobileSubTextColor={entity.mobileSubTextColor}
          textFontSize={calculatedFontSize.desktop}
          mobileTextFontSize={calculatedFontSize.mobile}
          subTextFontSize={entity.subTextFontSize}
          mobileSubTextFontSize={entity.mobileSubTextFontSize}
          textFontFamily={calculatedFontFamily.desktop}
          textFontWeight={calculatedFontWeight.desktop}
          textFontStyle={calculatedFontStyle.desktop}
          subTextFontFamily={entity.subTextFontFamily}
          subTextFontWeight={entity.subTextFontWeight}
          subTextFontStyle={entity.subTextFontStyle}
          mobileTextFontFamily={calculatedFontFamily.mobile}
          mobileTextFontWeight={calculatedFontWeight.mobile}
          mobileTextFontStyle={calculatedFontStyle.mobile}
          mobileSubTextFontFamily={entity.mobileSubTextFontFamily}
          mobileSubTextFontWeight={entity.mobileSubTextFontWeight}
          mobileSubTextFontStyle={entity.mobileSubTextFontStyle}
          boxShadow={entity.boxShadow}
          mobileBoxShadow={entity.mobileBoxShadow}
          lineHeight={calculatedLineHeight.desktop}
          mobileLineHeight={calculatedLineHeight.mobile}
          iconClassNameBefore={entity.iconClassNameBefore}
          iconClassNameAfter={entity.iconClassNameAfter}
          hover={{
            ...entity.hover,
            backgroundColor:
              entity?.hover?.backgroundColor || hoveredButtonBackgroundColor,
          }}
          mobileHover={{
            ...entity.mobileHover,
            backgroundColor:
              entity?.mobileHover?.backgroundColor ||
              entity?.hover?.backgroundColor ||
              hoveredButtonBackgroundColor,
          }}
        />
      </StyleWrapper>
    )
  }

  if (!isClient) return <></>

  switch (paymentMethod) {
    case paymentMethodTypes.RAZORPAY:
      return <RazorpayUpsellButton entity={entity} />
    case paymentMethodTypes.PAYPAL_SOAP:
      return <PaypalUpsellButton entity={entity} />
    case paymentMethodTypes.PAYPAL_REST:
      return <PaypalUpsellRestButton entity={entity} />
    case paymentMethodTypes.STRIPE_CARD:
      return <StripeCreditCardUpsellButton entity={entity} />
    case paymentMethodTypes.STRIPE_IDEAL:
    case paymentMethodTypes.STRIPE_BANCONTACT:
    case PaymentMethodEnum.StripeKlarna:
    case PaymentMethodEnum.StripeMultibanco:
      return <StripeSepaDebitUpsellButton entity={entity} />
    case paymentMethodTypes.DLOCAL_CARD:
      return <DLocalCreditCardUpsellButton entity={entity} />
    case paymentMethodTypes.FLUTTERWAVE:
      return <FlutterwaveUpsellButton entity={entity} />
    case paymentMethodTypes.MOLLIE:
      return <MollieUpsellButton entity={entity} />
    case paymentMethodTypes.GOCARDLESS:
      return <GocardlessUpsellButton entity={entity} />
    case paymentMethodTypes.XENDITIDCARD:
    case paymentMethodTypes.XENDITPHCARD:
      return <XenditUpsellButton entity={entity} />
    case paymentMethodTypes.CASH_ON_DELIVERY:
      return <CashOnDeliveryUpsellButton entity={entity} />
    case paymentMethodTypes.PAYSTACK_GH_CARD:
    case paymentMethodTypes.PAYSTACK_KE_CARD:
    case paymentMethodTypes.PAYSTACK_NG_CARD:
    case paymentMethodTypes.PAYSTACK_ZA_CARD:
    case paymentMethodTypes.PAYSTACK_EG_CARD:
    case paymentMethodTypes.PAYSTACK_CI_CARD:
      return <PaystackUpsellButton entity={entity} />
    case PaymentMethodEnum.MercadoPagoCard:
    case PaymentMethodEnum.MercadoPagoBrCard:
    case PaymentMethodEnum.MercadoPagoArCard:
    case PaymentMethodEnum.MercadoPagoClCard:
    case PaymentMethodEnum.MercadoPagoMxCard:
    case PaymentMethodEnum.MercadoPagoCoCard:
    case PaymentMethodEnum.MercadoPagoPix:
    case PaymentMethodEnum.MercadoPagoBrPix:
    case PaymentMethodEnum.MercadoPagoBoleto:
    case PaymentMethodEnum.MercadoPagoBrBoleto:
      return <MercadoPagoUpsellButton entity={entity} />
    case PaymentMethodEnum.SecureAndPay:
      return <SecureAndPayUpsellButton entity={entity} />
    default:
      // payment method not supported
      return <div />
  }
}

export default UpsellPaymentButton
